import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import dayjs from 'dayjs'
import specializedReportService, { specializedAttachFormats } from '@api/v1/specializedReportService'
import reportJobService from '@api/v1/reportJobService'
import useLatestRun from '@hooks/useLatestRun'
import SpecializedReportShell from '@components/reports/SpecializedReportShell'
import { feeReceiptsSavedCriteria } from '@components/reports/savedRunCriteria'
import SpecializedFilterBar from '@components/reports/SpecializedFilterBar'
import { objectField, dateRangeField } from '@components/reports/specializedFilterFields'
import ReportViewsMenu from '@components/reports/ReportViewsMenu'
import useSavedReportView from '@components/reports/useSavedReportView'
import { describeFilters, dateRangePart } from '@components/reports/reportFilterSummary'
import CsvReportGrid from '@components/reports/CsvReportGrid'

const TYPE_USERS = 1

// CC-50020: CB deserializes this into the shared RevenueAndCommissionsReportFilter
// but Fee Receipts only reads paymentReceivedDateStart/End (proc params) and
// userId (post-query originator match). invoiceDateStart/End, companyId/companyName
// and dateType bind and are then ignored. Send exactly the keys V1 sends.
//
// `omitAll` is load-bearing, not cosmetic: null payment dates make CB substitute
// 1753-01-01 → today, so an "All Dates" affordance here would invite an
// all-time run whose figures are WRONG rather than merely unfiltered — the exact
// defect CC-50020 fixed. The bespoke bar expressed this as DatePresetSelect's
// `omitAllDates`; the field spec carries the same guarantee.
const FIELDS = [
  objectField({ key: 'originator', label: 'Originator', typeId: TYPE_USERS, idKey: 'userId', nameKey: 'userName' }),
  dateRangeField({
    key: 'paymentReceived',
    label: 'Payment Received Date',
    startKey: 'paymentReceivedDateStart',
    endKey: 'paymentReceivedDateEnd',
    omitAll: true,
  }),
]

const makeDefaults = () => ({
  userId: null, userName: '',
  paymentReceivedDateStart: dayjs().startOf('year').format('YYYY-MM-DD'),
  paymentReceivedDateEnd: dayjs().format('YYYY-MM-DD'),
})

// Module-level so useLatestRun's `run` keeps one identity (spec 131).
const runErrorMessage = (err) => err?.response?.data?.error || err.message || 'Report failed'

const FeeReceiptsReport = ({ reportId, name = 'Fee Receipts by Originator', summary, jobId }) => {
  const [filters, setFilters] = useState(makeDefaults)
  // Spec 131: only the NEWEST run may write the rows, the spinner, the error or
  // the documents' criteria. Measured on QA: the split button's "Run report now"
  // started a second run mid-run, and the superseded 2026-YTD run's $289,871.50
  // replaced the newer Last Year $2,939,165.32 under a panel reading Last Year.
  // That item is disabled mid-run now too; this gate is the structural guarantee.
  const {
    running, error, ran, runKey, run: startRun,
  } = useLatestRun({ initialRunning: !!jobId, errorMessage: runErrorMessage })
  // CC-50013 (G3): saved views. The hook carries the grid's page state, so
  // the menu cannot be mounted without it (the CC-49732 defect).
  const view = useSavedReportView(setFilters)
  // CC-50029: exports state the period they cover.
  const filtersSummary = useMemo(() => describeFilters(dateRangePart(filters.paymentReceivedDateStart, filters.paymentReceivedDateEnd)), [filters])
  const [data, setData] = useState({ headers: [], rows: [], meta: [] })
  const [savedAsOf, setSavedAsOf] = useState(null)
  // Spec 185 (CC-53435): the reopened job's criteria line, built from ITS stored params
  // at the reopen's commit (null = the shell's "not recorded"). Never seeded into
  // `filters`: the filter bar is not the saved run's, and a reopen must not start a run.
  const [savedCriteria, setSavedCriteria] = useState(null)
  // Consume-once: the first run reopens the tray's saved job; later runs are fresh.
  const jobRef = useRef(jobId || null)

  const runReport = useCallback(() => startRun(async () => {
    const jid = jobRef.current
    jobRef.current = null // consumed — subsequent runs are fresh
    if (jid) {
      // Tray "View report" → reopen the exact saved snapshot + show its run time.
      const [st, r] = await Promise.all([reportJobService.status(jid), reportJobService.result(jid)])
      return () => { setSavedAsOf(st?.createdAt || null); setSavedCriteria(feeReceiptsSavedCriteria(st?.savedParams)); setData(r?.data || { headers: [], rows: [], meta: [] }) }
    }
    const result = await specializedReportService.feeReceipts.run(filters, { reportId })
    // Spec 131 decision 3: the saved-result banner clears at the COMMIT.
    return () => { setSavedAsOf(null); setData(result || { headers: [], rows: [], meta: [] }) }
  }, { criteria: { filtersSummary } }), [filters, reportId, startRun, filtersSummary])

  // Spec 131: deferred and cancellable, so StrictMode's simulated remount starts
  // ONE mount run, not two.
  useEffect(() => {
    const t = setTimeout(() => { runReport() }, 0)
    return () => clearTimeout(t)
  }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  // CC-50013 G2 / CC-50148: shared metadata-driven bar (see FIELDS).
  const filterBar = (
    <SpecializedFilterBar
      fields={FIELDS}
      filters={filters}
      onFiltersChange={setFilters}
      defaults={makeDefaults()}
      onRun={runReport}
      isLoading={running}
      queue={{
        reportId,
        name,
        actionKey: 'fee-receipts',
        endpoint: (f) => ({ method: 'POST', path: '/reports/fee-receipts', body: f }),
        // Spec 200: an emailed copy carries this report's own export, asked what the run asks.
        attachFormats: specializedAttachFormats('/reports/fee-receipts'),
      }}
      actions={(
        <ReportViewsMenu
          storageKey={'report:fee-receipts'}
          currentFilters={filters}
          onApply={view.applyView}
          getPageState={view.getPageState}
        />
      )}
    />
  )

  return (
    <SpecializedReportShell
      name={name} summary={summary} running={running} error={error}
      ranCriteria={ran?.criteria ?? null} runKey={runKey}
      filterBar={filterBar} filtersSummary={filtersSummary} summaryData={data} savedAsOf={savedAsOf}
      savedRunFiltersSummary={savedAsOf != null ? savedCriteria : undefined}
      download={(format, getToken) =>
        specializedReportService.feeReceipts.download(filters, format, getToken, `fee-receipts.${format}`)
      }
    >
      <CsvReportGrid key={view.gridKey} data={data} storageKey="report:fee-receipts" pageStateRef={view.pageStateRef} initialPageState={view.initialPageState} />
    </SpecializedReportShell>
  )
}

export default FeeReceiptsReport
